import { z } from 'zod'

const MAX_IMAGE_FILE_SIZE = 5 * 1024 * 1024 // 5 MB
const MIN_IMAGE_DIMENSIONS = { width: 200, height: 200 }
const MAX_IMAGE_DIMENSIONS = { width: 4096, height: 4096 }
const ACCEPTED_IMAGE_TYPES = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
]

export const SignInSchema = z.object({
    email: z
        .string()
        .min(1, { message: 'Email is required' })
        .email({ message: 'Please provide a valid email address.' }),

    password: z
        .string()
        .min(6, { message: 'Password must be at least 6 characters long.' })
        .max(100, { message: 'Password cannot exceed 100 characters.' }),
})

export const SignUpSchema = z.object({
    username: z
        .string()
        .min(3, { message: 'Username must be at least 3 characters long.' })
        .max(30, { message: 'Username cannot exceed 30 characters.' })
        .regex(/^[a-zA-Z0-9_]+$/, {
            message:
                'Username can only contain letters, numbers, and underscores.',
        }),

    name: z
        .string()
        .min(1, { message: 'Name is required.' })
        .max(50, { message: 'Name cannot exceed 50 characters.' })
        .regex(/^[a-zA-Z\s]+$/, {
            message: 'Name can only contain letters and spaces.',
        }),

    email: z
        .string()
        .min(1, { message: 'Email is required.' })
        .email({ message: 'Please provide a valid email address.' }),

    password: z
        .string()
        .min(6, { message: 'Password must be at least 6 characters long.' })
        .max(100, { message: 'Password cannot exceed 100 characters.' })
        .regex(/[A-Z]/, {
            message: 'Password must contain at least one uppercase letter.',
        })
        .regex(/[a-z]/, {
            message: 'Password must contain at least one lowercase letter.',
        })
        .regex(/[0-9]/, {
            message: 'Password must contain at least one number.',
        })
        .regex(/[^a-zA-Z0-9]/, {
            message: 'Password must contain at least one special character.',
        }),
})

export const NewsletterSubscriptionSchema = z.object({
    email: z
        .string()
        .min(1, { message: 'Email is required.' })
        .email({ message: 'Please provide a valid email address.' }),
})

export const ContactFormSchema = z.object({
    name: z.string().min(1, { message: 'Name is required.' }),
    email: z
        .string()
        .min(1, { message: 'Email is required' })
        .email({ message: 'Please provide a valid email address.' }),
    phone: z.string().optional(),
    message: z.string().min(1, { message: 'Message is required.' }),
})

export const CreatePostSchema = z.object({
    title: z
        .string()
        .min(5, { message: 'Title is required.' })
        .max(100, { message: 'Title cannot exceed 100 characters.' }),

    content: z.string().min(1, { message: 'Body is required.' }),

    type: z.enum(['blog', 'news']),

    tags: z
        .array(
            z
                .string()
                .min(1, { message: 'Tag is required.' })
                .max(30, { message: 'Tag cannot exceed 30 characters.' })
        )
        .min(1, { message: 'At least one tag is required.' })
        .max(3, { message: 'You cannot add more than 3 tags.' }),

    uploadedFeatureImage: z
        .string()
        .min(1, { message: 'Feature image is required.' }),

    // featureImageFile: z.instanceof(File, {
    //     message: 'Please select an image file.',
    // }),
    // .refine((file) => file.size <= MAX_IMAGE_FILE_SIZE, {
    //     message: `Image size must be less than ${MAX_IMAGE_FILE_SIZE / 1024 / 1024} MB.`,
    // })
    // .refine((file) => ACCEPTED_IMAGE_TYPES.includes(file.type), {
    //     message: 'Please select a valid image file. (JPEG, PNG or WebP).',
    // })
    // .refine(
    //     (file) =>
    //         new Promise((resolve) => {
    //             const reader = new FileReader()
    //             reader.onload = (e) => {
    //                 const img = new Image()
    //                 img.onload = () => {
    //                     const meetsDimensions =
    //                         img.width >= MIN_IMAGE_DIMENSIONS.width &&
    //                         img.height >= MIN_IMAGE_DIMENSIONS.height &&
    //                         img.width <= MAX_IMAGE_DIMENSIONS.width &&
    //                         img.height <= MAX_IMAGE_DIMENSIONS.height

    //                     resolve(meetsDimensions)
    //                 }
    //                 img.src = e.target?.result as string
    //             }
    //             reader.readAsDataURL(file)
    //         }),
    //     {
    //         message: `The image dimensions are invalid. Please upload an image between ${MIN_IMAGE_DIMENSIONS.width}x${MIN_IMAGE_DIMENSIONS.height} and ${MAX_IMAGE_DIMENSIONS.width}x${MAX_IMAGE_DIMENSIONS.height}.`,
    //     }
    // ),
})

export const UploadImageFileSchema = z.object({
    file: z
        .instanceof(File, {
            message: 'Please select an image file.',
        })
        .refine((file) => file.size <= MAX_IMAGE_FILE_SIZE, {
            message: `Image size must be less than ${MAX_IMAGE_FILE_SIZE / 1024 / 1024} MB.`,
        })
        .refine((file) => ACCEPTED_IMAGE_TYPES.includes(file.type), {
            message: 'Please select a valid image file. (JPEG, PNG or WebP).',
        })
        .refine(
            (file) =>
                new Promise((resolve) => {
                    const reader = new FileReader()
                    reader.onload = (e) => {
                        const img = new Image()
                        img.onload = () => {
                            const meetsDimensions =
                                img.width >= MIN_IMAGE_DIMENSIONS.width &&
                                img.height >= MIN_IMAGE_DIMENSIONS.height &&
                                img.width <= MAX_IMAGE_DIMENSIONS.width &&
                                img.height <= MAX_IMAGE_DIMENSIONS.height

                            resolve(meetsDimensions)
                        }
                        img.src = e.target?.result as string
                    }
                    reader.readAsDataURL(file)
                }),
            {
                message: `The image dimensions are invalid. Please upload an image between ${MIN_IMAGE_DIMENSIONS.width}x${MIN_IMAGE_DIMENSIONS.height} and ${MAX_IMAGE_DIMENSIONS.width}x${MAX_IMAGE_DIMENSIONS.height}.`,
            }
        ),
})

// export const AskQuestionSchema = z.object({
//     title: z
//         .string()
//         .min(5, { message: 'Title is required.' })
//         .max(100, { message: 'Title cannot exceed 100 characters.' }),

//     content: z.string().min(1, { message: 'Body is required.' }),

//     tags: z
//         .array(
//             z
//                 .string()
//                 .min(1, { message: 'Tag is required.' })
//                 .max(30, { message: 'Tag cannot exceed 30 characters.' })
//         )
//         .min(1, { message: 'At least one tag is required.' })
//         .max(3, { message: 'You cannot add more than 3 tags.' }),
// })

export const UserSchema = z.object({
    name: z.string().min(1, { message: 'Name is required.' }),

    username: z
        .string()
        .min(3, { message: 'Username must be at least 3 characters long.' }),

    email: z
        .string()
        .email({ message: 'Please provide a valid email address.' }),

    bio: z.string().optional(),

    image: z
        .string()
        .url({ message: 'Please provide a valid URL.' })
        .optional(),

    location: z.string().optional(),

    portfolio: z
        .string()
        .url({ message: 'Please provide a valid URL.' })
        .optional(),

    reputation: z.number().optional(),
})

export const AccountSchema = z.object({
    userId: z.string().min(1, { message: 'User ID is required.' }),

    name: z.string().min(1, { message: 'Name is required.' }),

    image: z
        .string()
        .url({ message: 'Please provide a valid URL.' })
        .optional(),

    password: z
        .string()
        .min(6, { message: 'Password must be at least 6 characters long.' })
        .max(100, { message: 'Password cannot exceed 100 characters.' })
        .regex(/[A-Z]/, {
            message: 'Password must contain at least one uppercase letter.',
        })
        .regex(/[a-z]/, {
            message: 'Password must contain at least one lowercase letter.',
        })
        .regex(/[0-9]/, {
            message: 'Password must contain at least one number.',
        })
        .regex(/[^a-zA-Z0-9]/, {
            message: 'Password must contain at least one special character.',
        })
        .optional(),

    provider: z.string().min(1, { message: 'Provider is required.' }),

    providerAccountId: z
        .string()
        .min(1, { message: 'Provider Account ID is required.' }),
})

export const SignInWithOAuthSchema = z.object({
    provider: z.enum(['apple', 'github', 'google']),

    providerAccountId: z
        .string()
        .min(1, { message: 'Provider Account ID is required.' }),

    user: z.object({
        name: z.string().min(1, { message: 'Name is required.' }),
        username: z.string().min(3, {
            message: 'Username must be at least 3 characters long.',
        }),
        email: z
            .string()
            .email({ message: 'Please provide a valid email address.' }),
        image: z.string().url('Invalid image URL').optional(),
    }),
})

export const EditPostSchema = CreatePostSchema.extend({
    postId: z.string().min(1, { message: 'Post ID is required.' }),
})

export const GetPostSchema = z.object({
    postId: z.string().min(1, { message: 'Post ID is required.' }),
})

export const PaginatedSearchParamsSchema = z.object({
    page: z.number().int().positive().default(1),
    pageSize: z.number().int().positive().default(10),
    query: z.string().optional(),
    filter: z.string().optional(),
    sort: z.string().optional(),
})

export const GetTagPostsSchema = PaginatedSearchParamsSchema.extend({
    tagId: z.string().min(1, { message: 'Tag is required.' }),
})
